import { CSSProperties, useEffect, useRef, useState } from "react";
import { cancelOrder, errorText, modifyOrder, placeLimit } from "./api";
import { useIbkrData, useVisible } from "./poller";
import { OrderInfo, QuoteRow } from "./types";
import { C, Chip, ContractTitle, PHASE_DOT, PHASE_LABEL, SideTag, StatusDot, UnderlyingQuote, WidgetBox, WindowPhase, companyName, fmt, fmtTotal, isDone, isWorking, PriceField, SizeField } from "./ui";
import { WidgetProps, closeModifyWindows } from "./windows";

// size / price changes of a working order are sent once the clicks pause
const MODIFY_DEBOUNCE_MS = 300;

// Limit order window. params:
//   new order:      {conId, side, size, price} — editable, Submit places it
//   existing order: {orderId, conId?, variant?: "modify"} — changes modify the order right away
// After Submit the new order's orderId is stored in params (submitted=true keeps
// the BUY / SELL row visible, disabled: an order's side can't be changed) and
// the window stays editable: the size / price fields modify the
// working order right away; bid / last / ask only set the price before Submit.
export function TicketWidget({ widget, actions }: WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useVisible(ref);
  const p = widget.params;
  const orderId: number | undefined = p.orderId;

  const orderData = useIbkrData<OrderInfo>("order", { orderId: orderId ?? -1, permId: p.permId }, visible && orderId != null);
  // the permId identifies the order at IB even after a backend reconnect
  const knownPermId = orderData.data?.permId;
  useEffect(() => {
    if (knownPermId && !p.permId) actions.patch({ permId: knownPermId });
  }, [knownPermId, p.permId, actions]);
  const order = orderId != null ? orderData.data : undefined;
  const conId: number | undefined = p.conId ?? order?.contract.conId;
  // an option's underlying is polled along (its conId is stored once known, so the request stays stable)
  const quoteData = useIbkrData<QuoteRow[]>("quotes", { conIds: p.underConId ? [conId ?? -1, p.underConId] : [conId ?? -1] }, visible && conId != null);
  const row = quoteData.data?.[0];
  const underRow = p.underConId ? quoteData.data?.[1] : undefined;
  const quote = row && "quote" in row ? row.quote : undefined;
  const contract = order?.contract ?? (row && "contract" in row ? row.contract : undefined);
  const knownUnderConId = contract?.underConId;
  useEffect(() => {
    if (knownUnderConId && !p.underConId) actions.patch({ underConId: knownUnderConId });
  }, [knownUnderConId, p.underConId, actions]);

  // local edits of a working order until the modification is confirmed
  const [draft, setDraft] = useState<{ size?: number; price?: number }>({});
  const [busy, setBusy] = useState(false);
  // in the params, not component state: the chat snapshot sent to the agent includes them
  const error: string | null = p.error ?? null;
  const setError = (e: string | null) => actions.patch({ error: e ?? undefined });
  const modifyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (modifyTimer.current) clearTimeout(modifyTimer.current); }, []);

  const phase: WindowPhase = orderId == null ? "preview" : order?.status ?? "pending";
  // the backend doesn't know the order (any more): nothing can be done with it
  const lost = orderId != null && !orderData.data && !!orderData.error;
  const working = isWorking(phase) && !lost;
  const done = isDone(phase);
  const side: "BUY" | "SELL" = order?.side ?? p.side ?? "BUY";
  const size: number = draft.size ?? (order ? order.size : p.size);
  const price: number = draft.price ?? (order ? order.price ?? 0 : p.price);
  const editable = phase === "preview" || working;

  const setValue = (change: { size?: number; price?: number }) => {
    if (phase === "preview") {
      actions.patch(change);
      return;
    }
    if (!working || orderId == null) return;
    const next = { ...draft, ...change };
    setDraft(next);
    if (modifyTimer.current) clearTimeout(modifyTimer.current);
    modifyTimer.current = setTimeout(async () => {
      try {
        await modifyOrder(orderId, next);
        setError(null);
      } catch (e) {
        setError(errorText(e));
      } finally {
        setDraft({});
      }
    }, MODIFY_DEBOUNCE_MS);
  };

  const submit = async () => {
    if (conId == null) return;
    setBusy(true);
    try {
      const placed = await placeLimit(conId, side === "BUY" ? size : -size, price);
      actions.patch({ orderId: placed.orderId, permId: placed.permId || undefined, submitted: true });
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (orderId == null) return;
    setBusy(true);
    try {
      await cancelOrder(orderId);
      // like in the design, a cancelled order's modify windows go away with it
      if (p.variant === "modify") closeModifyWindows(actions, orderId);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // before Submit: estimated total at the ticket's limit price × size × multiplier (100 for options)
  const estimate = phase === "preview" && contract && price > 0 ? `Est. ${fmtTotal(price * size * contract.multiplier)}` : "";
  const filledText = order ? `Filled ${order.filled} / ${order.size}${order.filled > 0 && order.avgFillPrice ? ` at ${fmt(order.avgFillPrice)}` : ""}` : estimate;
  // IB's last error/warning for the order (a rejection, a server-side cancel, a
  // rejected modification, a warning on a working order); warnings don't stand out
  const message = error ?? (phase !== "filled" ? order?.message : null) ?? orderData.error ?? null;
  const fills = order && order.filled > 0 ? `${filledText} · ` : "";
  const caption = message
    ? <>{fills}<span style={{ color: message.startsWith("Warning") ? C.muted : C.cancelled }}>{message}</span></>
    : filledText;

  const status = <StatusDot color={PHASE_DOT[phase]} label={PHASE_LABEL[phase]} />;
  const header = <ContractTitle contract={contract} side={side} />;
  const duplicate = () => actions.duplicate({ conId, side, size, price });

  const sizeField = (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 12.5 }}>Size:</span>
      <SizeField size={size} disabled={!editable || busy} onChange={(v) => setValue({ size: v })}
        note={(v) => (contract && price > 0 ? fmtTotal(price * v * contract.multiplier, 0) : null)} />
    </div>
  );
  const priceField = (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 12.5 }}>Price:</span>
      <PriceField price={price} disabled={!editable || busy} onChange={(v) => setValue({ price: v })}
        note={(v) => (contract ? fmtTotal(v * size * contract.multiplier, 0) : null)} />
    </div>
  );

  // "Modify order" window of the active orders list
  if (p.variant === "modify") {
    return (
      <WidgetBox boxRef={ref} title={<span><SideTag side={side} /> <Chip>{contract?.label ?? "…"}</Chip> {companyName(contract)}</span>}
        status={status} onDuplicate={() => actions.duplicate({ ...p, error: undefined })} onClose={actions.close}>
        <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 12 }}>{sizeField}{priceField}</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 11.5, color: C.muted, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{caption}</span>
            <span style={{ display: "flex", gap: 8 }}>
              <button onClick={actions.close} style={{ height: 36, padding: "0 16px", background: "#fff", color: C.text, border: `1px solid ${C.border}`, fontSize: 12.5 }}>Close</button>
              <button onClick={cancel} disabled={!working || busy} style={{ height: 36, padding: "0 16px", background: working ? C.sell : C.done, color: "#fff", border: 0, fontSize: 12.5 }}>
                {done ? PHASE_LABEL[phase][0].toUpperCase() + PHASE_LABEL[phase].slice(1) : "Cancel order"}
              </button>
            </span>
          </div>
        </div>
      </WidgetBox>
    );
  }

  const sideButton = (s: "BUY" | "SELL"): CSSProperties => {
    const selected = s === side;
    const color = s === "BUY" ? C.buy : C.sell;
    return { flex: 1, minWidth: 0, height: 32, background: selected ? color : "#fff", color: selected ? "#fff" : C.text, border: `1px solid ${C.border}`, fontSize: 12.5, fontWeight: 600 };
  };
  const setPriceButton: CSSProperties = { flex: 1, minWidth: 0, height: 36, background: "#fff", color: C.text, border: `1px solid ${C.border}`, fontSize: 12.5 };
  const actLabel = phase === "preview" ? "Submit" : working ? "Cancel" : PHASE_LABEL[phase][0].toUpperCase() + PHASE_LABEL[phase].slice(1);
  const actBg = phase === "preview" ? C.buy : working ? C.sell : C.done;

  return (
    <WidgetBox boxRef={ref} title={header} status={status} onDuplicate={duplicate} onClose={actions.close}>
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        {(phase === "preview" || p.submitted) && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => actions.patch({ side: "BUY" })} disabled={busy || phase !== "preview"} style={sideButton("BUY")}>BUY</button>
            <button onClick={() => actions.patch({ side: "SELL" })} disabled={busy || phase !== "preview"} style={sideButton("SELL")}>SELL</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 12 }}>{sizeField}{priceField}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {(["bid", "last", "ask"] as const).map((k) => (
            <button key={k} disabled={phase !== "preview" || busy || quote?.[k] == null} style={setPriceButton}
              onClick={() => quote?.[k] != null && setValue({ price: quote[k]! })}>
              {k} {fmt(quote?.[k])}
            </button>
          ))}
          <button onClick={phase === "preview" ? submit : cancel} disabled={busy || done || conId == null}
            style={{ flex: "none", height: 36, padding: "0 16px", background: actBg, color: "#fff", border: 0, fontSize: 12.5 }}>
            {actLabel}
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, minHeight: 18 }}>
          <UnderlyingQuote row={underRow} />
          <span style={{ fontSize: 11.5, color: C.muted, textAlign: message ? "left" : "right", minWidth: 0, overflowWrap: "anywhere" }}>{caption}</span>
        </div>
      </div>
    </WidgetBox>
  );
}
